import { useState } from "react";

import { File, Info, Loader2, Trash2 } from "lucide-react";
import type PartySocket from "partysocket";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

import { DocumentMetadata } from "./DocumentMetadata";
import type { KnowledgeDocumentWithTask } from "./types/Document";

export interface DocumentListProps {
  documents: KnowledgeDocumentWithTask[];
  isLoading: boolean;
  isError: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  partySocket: PartySocket;
}

const LoadingState = () => (
  <div className="flex h-full items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin" />
  </div>
);

const ErrorState = () => (
  <div className="flex h-full items-center justify-center text-red-500">
    Error loading documents. Please try again.
  </div>
);

const EmptyState = () => (
  <div className="flex h-full items-center justify-center text-sm text-gray-500">
    <div className="py-8">Upload your first document</div>
  </div>
);

const TaskStatus = ({
  task,
}: {
  task: NonNullable<KnowledgeDocumentWithTask["task"]>;
}) => {
  if (task.status === "uploading" || task.status === "processing") {
    return (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {task.status === "uploading" ? "Uploading" : "Processing"} (
        {task.progress.toFixed(2)}%)
      </>
    );
  }

  if (task.status === "completed") return "Completed";
  if (task.status === "error") return "Error";
  return null;
};

const ContentState = ({
  isLoading,
  isError,
  documents,
  isDeleting,
  onDelete,
  partySocket,
}: DocumentListProps) => {
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;
  if (documents.length === 0) return <EmptyState />;

  return (
    <Table>
      <TableBody>
        {documents.map((document) => (
          <DocumentRow
            key={document.id}
            document={document}
            isDeleting={isDeleting}
            onDelete={onDelete}
            partySocket={partySocket}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export function DocumentList(props: Readonly<DocumentListProps>) {
  return (
    <div className="flex h-full flex-col">
      <Card className="flex-grow overflow-hidden">
        <ScrollArea className="h-full">
          <div>
            <ContentState {...props} />
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
}

const DocumentRow = ({
  document,
  isDeleting,
  onDelete,
  partySocket,
}: {
  document: KnowledgeDocumentWithTask;
  isDeleting: boolean;
  onDelete: (id: string) => void;
  partySocket: PartySocket;
}) => {
  const [showMetadata, setShowMetadata] = useState(false);

  return (
    <TableRow>
      <TableCell className="w-1/2">
        <File className="-mt-1 mr-2 inline-block h-4 w-4" />
        {document.name}
      </TableCell>
      <TableCell className="w-1/3 text-right text-xs text-gray-500">
        {document.task ? (
          <span className="flex items-center justify-end">
            <TaskStatus task={document.task} />
          </span>
        ) : (
          new Date(document.uploadedAt).toLocaleString()
        )}
      </TableCell>
      <TableCell className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowMetadata(true)}
          className="h-8 w-8"
        >
          <Info className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(document.key)}
          disabled={isDeleting}
          className="h-8 w-8 text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
        <DocumentMetadata
          document={document}
          isOpen={showMetadata}
          onClose={() => setShowMetadata(false)}
          partySocket={partySocket}
        />
      </TableCell>
    </TableRow>
  );
};
